import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://marinconstruesfuerzo.com';

const cookiesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/politica-de-cookies`,
  url: `${siteUrl}/politica-de-cookies`,
  name: 'Política de Cookies | Marin Construesfuerzo SL',
  description: 'Política de cookies de Marin Construesfuerzo SL. Información sobre los tipos de cookies utilizadas y cómo gestionarlas.',
  inLanguage: 'es-ES',
  isPartOf: { '@id': `${siteUrl}/#website` },
  publisher: {
    '@type': 'Organization',
    name: 'Marin Construesfuerzo SL',
    url: siteUrl,
  },
};

export default function CookiesPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Política de Cookies | Marin Construesfuerzo SL"
        description="Política de cookies de Marin Construesfuerzo SL — Tipos de cookies utilizadas, finalidad y cómo gestionarlas. Empresa de construcción en Arrecife, Lanzarote."
        canonical={`${siteUrl}/politica-de-cookies`}
        jsonLd={cookiesJsonLd}
      />
      <Navbar />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Política de <strong className="text-amber-500">Cookies</strong>
          </h1>
          
          <p className="text-gray-600 mb-8">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. ¿Qué son las Cookies?</h2>
              <p className="mb-4">
                Las cookies son pequeños archivos de texto que se almacenan en su dispositivo (ordenador, tablet o móvil) cuando visita nuestro sitio web. Estas cookies permiten que el sitio web reconozca su dispositivo y recuerde información sobre su visita, como sus preferencias y configuraciones.
              </p>
              <p>
                Las cookies son ampliamente utilizadas para hacer que los sitios web funcionen de manera más eficiente y proporcionar información a los propietarios del sitio.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. ¿Cómo Utilizamos las Cookies?</h2>
              <p className="mb-4">
                <strong>Marin Construesfuerzo SL</strong> utiliza cookies para mejorar la experiencia del usuario en nuestro sitio web y para entender mejor cómo los visitantes interactúan con nuestros servicios de <strong>construcción en Lanzarote</strong>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Tipos de Cookies que Utilizamos</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.1. Cookies Técnicas (Necesarias)</h3>
                  <p className="mb-2">
                    Estas cookies son esenciales para el funcionamiento del sitio web y no pueden ser desactivadas en nuestros sistemas. Generalmente se establecen en respuesta a acciones realizadas por usted, como establecer sus preferencias de privacidad, iniciar sesión o completar formularios.
                  </p>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Finalidad:</strong> Permitir la navegación y el uso de las funcionalidades básicas del sitio web.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Duración:</strong> Sesión o persistentes
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.2. Cookies de Rendimiento y Analíticas</h3>
                  <p className="mb-2">
                    Estas cookies nos permiten contar las visitas y fuentes de tráfico para poder medir y mejorar el rendimiento de nuestro sitio. Nos ayudan a saber qué páginas son las más y las menos populares, y a ver cómo los visitantes navegan por el sitio.
                  </p>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Finalidad:</strong> Analizar el comportamiento de los usuarios y mejorar la experiencia del sitio web.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Duración:</strong> Hasta 2 años
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Ejemplos:</strong> Google Analytics
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.3. Cookies de Funcionalidad</h3>
                  <p className="mb-2">
                    Estas cookies permiten que el sitio web recuerde las elecciones que hace (como su nombre de usuario, idioma o la región en la que se encuentra) y proporcionan características mejoradas y más personales.
                  </p>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Finalidad:</strong> Recordar sus preferencias y personalizar su experiencia.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Duración:</strong> Hasta 1 año
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3.4. Cookies de Publicidad y Marketing</h3>
                  <p className="mb-2">
                    Estas cookies pueden ser establecidas a través de nuestro sitio por nuestros socios publicitarios. Pueden ser utilizadas por esas empresas para crear un perfil de sus intereses y mostrarle anuncios relevantes en otros sitios.
                  </p>
                  <p className="text-sm text-gray-600 mt-3">
                    <strong>Finalidad:</strong> Mostrar publicidad relevante sobre nuestros servicios de <strong>construcción y reformas en Arrecife</strong>.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Duración:</strong> Hasta 2 años
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies de Terceros</h2>
              <p className="mb-4">
                Además de nuestras propias cookies, también utilizamos cookies de terceros para los siguientes propósitos:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Google Analytics:</strong> Para analizar el uso del sitio web y mejorar nuestros servicios</li>
                <li><strong>Google Maps:</strong> Para mostrar mapas interactivos de nuestra ubicación en Lanzarote</li>
                <li><strong>Redes Sociales:</strong> Para permitir compartir contenido en redes sociales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Base Legal para el Uso de Cookies</h2>
              <p className="mb-4">
                El uso de cookies se basa en:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Consentimiento:</strong> Para cookies no esenciales, solicitamos su consentimiento explícito</li>
                <li><strong>Interés legítimo:</strong> Para cookies técnicas necesarias para el funcionamiento del sitio</li>
                <li><strong>Cumplimiento legal:</strong> Para cumplir con la Ley de Servicios de la Sociedad de la Información y Comercio Electrónico (LSSI-CE)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. ¿Cómo Gestionar las Cookies?</h2>
              <p className="mb-4">
                Usted puede controlar y/o eliminar las cookies como desee. Puede eliminar todas las cookies que ya están en su dispositivo y puede configurar la mayoría de los navegadores para evitar que se instalen.
              </p>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4">
                <p className="text-sm">
                  <strong>Nota importante:</strong> Si elimina o rechaza las cookies, algunas funcionalidades del sitio web pueden no funcionar correctamente.
                </p>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 mt-6">Configuración por Navegador:</h3>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <i className="ri-chrome-fill text-2xl text-gray-600 mt-1"></i>
                  <div>
                    <strong>Google Chrome:</strong>
                    <p className="text-sm text-gray-600">Configuración &gt; Privacidad y seguridad &gt; Cookies y otros datos de sitios</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <i className="ri-firefox-fill text-2xl text-gray-600 mt-1"></i>
                  <div>
                    <strong>Mozilla Firefox:</strong>
                    <p className="text-sm text-gray-600">Opciones &gt; Privacidad y seguridad &gt; Cookies y datos del sitio</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <i className="ri-safari-fill text-2xl text-gray-600 mt-1"></i>
                  <div>
                    <strong>Safari:</strong>
                    <p className="text-sm text-gray-600">Preferencias &gt; Privacidad &gt; Cookies y datos de sitios web</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <i className="ri-edge-fill text-2xl text-gray-600 mt-1"></i>
                  <div>
                    <strong>Microsoft Edge:</strong>
                    <p className="text-sm text-gray-600">Configuración &gt; Privacidad, búsqueda y servicios &gt; Cookies y permisos del sitio</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies Utilizadas en Este Sitio Web</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 border-b">Nombre</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 border-b">Tipo</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 border-b">Duración</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-gray-900 border-b">Finalidad</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr className="border-b">
                      <td className="px-4 py-3">_ga</td>
                      <td className="px-4 py-3">Analítica</td>
                      <td className="px-4 py-3">2 años</td>
                      <td className="px-4 py-3">Distinguir usuarios únicos</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3">_gid</td>
                      <td className="px-4 py-3">Analítica</td>
                      <td className="px-4 py-3">24 horas</td>
                      <td className="px-4 py-3">Distinguir usuarios únicos</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3">_gat</td>
                      <td className="px-4 py-3">Analítica</td>
                      <td className="px-4 py-3">1 minuto</td>
                      <td className="px-4 py-3">Limitar la tasa de solicitudes</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">cookie_consent</td>
                      <td className="px-4 py-3">Técnica</td>
                      <td className="px-4 py-3">1 año</td>
                      <td className="px-4 py-3">Recordar preferencias de cookies</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Actualización de la Política de Cookies</h2>
              <p>
                Podemos actualizar nuestra Política de Cookies ocasionalmente. Le notificaremos cualquier cambio publicando la nueva Política de Cookies en esta página y actualizando la fecha de "última actualización" en la parte superior.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Más Información</h2>
              <p className="mb-4">
                Para obtener más información sobre las cookies y cómo gestionarlas, puede visitar:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline cursor-pointer">www.aboutcookies.org</a></li>
                <li><a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline cursor-pointer">www.allaboutcookies.org</a></li>
                <li><a href="https://www.youronlinechoices.eu" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline cursor-pointer">www.youronlinechoices.eu</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contacto</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="mb-4">
                  Si tiene alguna pregunta sobre nuestra Política de Cookies o sobre cómo utilizamos las cookies en nuestro sitio web de <strong>servicios de construcción en Lanzarote</strong>, puede contactarnos:
                </p>
                <p className="mb-1"><strong>Marin Construesfuerzo SL</strong></p>
                <p className="mb-1">NIF: B26637975</p>
                <p className="mb-1">Dirección: CL Victor Hugo, 91, 35500 Arrecife, Las Palmas, Lanzarote, España</p>
                <p className="mb-1">Teléfono: <a href="tel:+34613725305" className="text-amber-500 hover:underline cursor-pointer">613 72 53 05</a></p>
                <p className="mb-1">Email: <a href="mailto:m.construesfuerzo@gmail.com" className="text-amber-500 hover:underline cursor-pointer">m.construesfuerzo@gmail.com</a></p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
